import {
	createFunctionLibrary,
	deStringifyExperiments,
	buildDeepStructure,
	flattenStructure,
	getRequestedPageCompId,
} from '@wix/thunderbolt-becky-root'
import type { BeckyRequestPayload, SiteAssetsModule } from '@wix/thunderbolt-becky-root'
import type { Data, Structure } from '@wix/thunderbolt-becky-types'
import { resolveBeckyModel } from '../siteAssetsModules/helpers/resolveBeckyModel'
import { resolveByRefComponents } from './resolveByRefComponents'
import { getBeckyTopology } from '../siteAssetsModules/helpers/topologyBuilder'
import { resolveAppWidgetsGhosts } from './appWidgetGhosts/appWidgetsGhosts'
import { BeckyFetch } from '../siteAssetsModules/utils/beckyFetch'
import { resolveSvgItemsFactory } from '../siteAssetsModules/helpers/resolveSvgItems'

export const resolveByRef = resolveByRefComponents

const byRefModule: SiteAssetsModule<BeckyRequestPayload> = (moduleContext) => {
	const { httpClient, metricsReporter, topologyGetter, logger } = moduleContext
	const fetchFn = BeckyFetch(httpClient.fetch, metricsReporter, logger)
	const functionLibrary = createFunctionLibrary({
		fetch: fetchFn,
		metricsReporter,
	})

	const resolveAndAssignSvgItems = resolveSvgItemsFactory({ functionLibrary, fetch: fetchFn, logger })

	async function execute(payload: BeckyRequestPayload) {
		const { beckyExperiments, pages } = payload

		const experiments = deStringifyExperiments(beckyExperiments)
		const beckyModel = await resolveBeckyModel({
			payload,
			beckyTopology: getBeckyTopology(
				topologyGetter,
				payload,
				!!experiments['specs.thunderbolt.relativeMediaRoot']
			),
			fetchFn,
			metricsReporter,
		})

		const masterPage = await pages.masterPage

		// AppWidgets may contain deleted RefComponents (which will be resolved next), so AppWidgets must be resolved first
		return resolveAppWidgetsGhosts(
			beckyModel,
			flattenStructure(masterPage.structure, payload.viewMode === 'mobile', masterPage.data.slots),
			masterPage.data,
			functionLibrary,
			payload,
			logger
		)
			.then(
				({
					structure: withAppWidgetsGhostsStructure,
					data: withAppWidgetsGhostsData,
					masterPageStructure: withAppWidgetsGhostsMasterPageStructure,
					masterPageData: withAppWidgetsGhostsMasterPageData,
				}) => {
					return resolveByRefComponents(
						{ ...beckyModel, structure: withAppWidgetsGhostsStructure, data: withAppWidgetsGhostsData },
						withAppWidgetsGhostsMasterPageStructure,
						withAppWidgetsGhostsMasterPageData,
						functionLibrary,
						payload
					)
				}
			)
			.then(async ({ structure, data, translations }) => {
				await resolveAndAssignSvgItems(data, beckyModel.environmentTopology)
				return { structure, data, translations }
			})
			.then(async ({ structure, data, translations }) => {
				const resolvedStructure = resolveSlots(structure, data.slots)
				const requestedPageCompId = getRequestedPageCompId(pages)
				const currentPage = await pages[requestedPageCompId]

				pages[requestedPageCompId] = Promise.resolve({
					...currentPage,
					data,
					translations,
					structure: buildDeepStructure(resolvedStructure[requestedPageCompId], resolvedStructure),
				})

				return payload
			})
	}

	return {
		execute: (payload) => metricsReporter.runAsyncAndReport(() => execute(payload), 'tb-byref-module'),
	}
}

export default byRefModule

// TODO should probably happen not here but in a common place between public and ds, and not within the context of byref
const resolveSlots = (structure: Structure, slotsData: Data['slots']) => {
	return Object.values(structure)
		.filter((comp) => comp.slotsQuery && slotsData[comp.slotsQuery])
		.map((comp) => {
			// @ts-ignore we filtered out comps w/o slotsQuery
			return { ...comp, slots: slotsData[comp.slotsQuery].slots }
		})
		.reduce((acc, val) => {
			return { ...acc, [val.id]: val }
		}, structure)
}
